import { RefObject, useEffect, useRef } from 'react';
import { getCognitoTokens } from '../components/auth/cognito/configureAmplify';
import { serialiseIdentity } from '../utils/analytics/identity';
import {
  EMBED_AUTH_REQUEST_TYPE,
  EMBED_AUTH_TOKEN_TYPE,
  isTrustedMsr2Origin,
  onEmbedSignOut,
} from '../utils/embedAuth';
import { deriveAnalyticsIdentity } from './useAnalyticsIdentity';
import { useCurrentUserAuth } from './useCurrentUserAuth';

// Re-send well before the token actually expires so the embed never makes a
// call with a stale one, and never sooner than this if the token is already
// near expiry (guards against a tight re-post loop on clock skew).
const REFRESH_LEAD_MS = 60_000;

// The session was unreadable this time round. Come back sooner than a normal
// refresh, but not so fast that a persistent outage becomes a busy loop.
const RETRY_AFTER_FAILURE_MS = 30_000;

// Never trust the gap between a server-issued `exp` and the local clock to be
// accurate: a slow client clock would push the re-send past the real expiry.
const MAX_REFRESH_INTERVAL_MS = 15 * 60_000;

/**
 * Answers an embedded MSR2 frame's request for MSR3's Cognito session.
 *
 * MSR2 cannot obtain its own session inside a cross-site iframe (see
 * `utils/embedAuth.ts` and APP-2221), so it asks its parent instead. MSR3
 * replies with the access + id token and keeps re-sending as the token rolls
 * over, so a long-lived embed never falls out of auth.
 *
 * @param iframeRef - the embed's iframe; used to prove a message came from it
 * @param isEnabled - false while there is no iframe to talk to
 */
export const useEmbedAuthHandshake = (
  iframeRef: RefObject<HTMLIFrameElement | null>,
  isEnabled = true,
) => {
  const { currentUser } = useCurrentUserAuth();
  const identity = deriveAnalyticsIdentity(currentUser);

  // Latest derived identity, readable from the message-handling effect below
  // without adding `identity` to its dependency array (that array must stay
  // as-is -- see the effect's own comment). Written during render, which is
  // fine: this ref is only ever read from callbacks/effects, never during
  // render itself.
  const identityRef = useRef(identity);

  identityRef.current = identity;

  // Whether identity fields have ever actually gone out to the frame on
  // this mount. This is what lets an absent identity later be told apart
  // from an absent identity now: the first is a sign-out (send
  // `identity_cleared`), the second is "not resolved yet" (send nothing).
  // A fresh `useRef` per mount means a remount can never inherit a
  // previous mount's "already sent" state.
  const identitySentRef = useRef(false);

  // The origin of the last request we actually validated and replied to.
  // Re-sends triggered by identity resolving late only ever target this --
  // never anything else -- so a resend can't become a path to post to an
  // origin that hasn't already passed the trust + source checks.
  const lastValidatedOriginRef = useRef<null | string>(null);

  // Lets the identity-resend effect below call back into the current
  // `sendTokens` closure without pulling it into that effect's own
  // dependency array (which must stay `[isEnabled, currentUser]`, matching
  // the change-detection this mirrors in useAnalyticsIdentity.ts).
  const sendTokensRef = useRef<(targetOrigin: string) => Promise<void>>();

  useEffect(() => {
    if (!isEnabled) return;

    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = (targetOrigin: string, delayMs: number) => {
      if (cancelled) return;
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => void sendTokens(targetOrigin), delayMs);
    };

    // MSR2 re-asks every 400ms until it gets an answer, so several requests can be
    // in flight at once. Share one fetch between them rather than hitting
    // fetchAuthSession() (and its warn) once per ask.
    let inFlight: ReturnType<typeof getCognitoTokens> | undefined;

    const fetchTokens = () => {
      if (!inFlight) {
        inFlight = getCognitoTokens().finally(() => {
          inFlight = undefined;
        });
      }

      return inFlight;
    };

    const sendTokens = async (targetOrigin: string) => {
      if (cancelled) return;

      const tokens = await fetchTokens();

      if (cancelled) return;

      // A transient `fetchAuthSession()` failure must not end the refresh
      // chain: this timer is the only thing keeping a long-lived embed in
      // auth, and returning here would silently strand it until the embed
      // 401s and re-asks. Come back and try again instead.
      if (!tokens) {
        scheduleNext(targetOrigin, RETRY_AFTER_FAILURE_MS);

        return;
      }

      // The frame can detach while this component stays mounted. Re-arm rather
      // than return: the chain is the only thing keeping a long-lived embed in
      // auth, and a dead chain is silent.
      const frame = iframeRef.current?.contentWindow;

      if (!frame) {
        scheduleNext(targetOrigin, RETRY_AFTER_FAILURE_MS);

        return;
      }

      // Identity is best-effort: `currentUser` may not have loaded yet, in
      // which case there is nothing to add and the token send must not wait
      // for it -- the handshake is what keeps a live embed signed in. Spread
      // rather than a nested key so MSR2's GTM can read `user_id` /
      // `is_external` as plain top-level fields; omitted entirely (not sent
      // as `undefined`) when there is no identity yet.
      //
      // But once identity fields HAVE gone out at least once, an absence
      // from then on means the member signed out, not "still loading" --
      // send `identity_cleared: true` instead of just dropping the keys, so
      // MSR2 can tell a sign-out apart from a not-yet-resolved identity (see
      // utils/embedAuth.ts for the full contract).
      let identityFields: Record<string, unknown>;

      if (identityRef.current) {
        identityFields = identityRef.current;
        identitySentRef.current = true;
      } else if (identitySentRef.current) {
        identityFields = { identity_cleared: true };
      } else {
        identityFields = {};
      }

      // Exact origin, never '*'. A wildcard here would hand the access token to
      // whatever happened to be loaded in the frame.
      frame.postMessage(
        { type: EMBED_AUTH_TOKEN_TYPE, ...tokens, ...identityFields },
        targetOrigin,
      );

      scheduleNext(
        targetOrigin,
        Math.min(
          Math.max(
            tokens.expiresAt - Date.now() - REFRESH_LEAD_MS,
            REFRESH_LEAD_MS,
          ),
          MAX_REFRESH_INTERVAL_MS,
        ),
      );
    };

    sendTokensRef.current = sendTokens;

    // `managedLoginSignOut` is a plain non-React function with no access to
    // this hook's refs, and by the time it (or any later scheduled resend)
    // calls `getCognitoTokens()` after Amplify's `signOut()` has run, that
    // call returns `null` -- there would be no tokens left to ride a clear
    // on, and `currentUser` never transitions either (signing out doesn't
    // reset Redux's `userMe`), so the resend effect below never fires for
    // this. Register a handler `managedLoginSignOut` can call directly,
    // BEFORE it calls `signOut()`, while the session (and so
    // `getCognitoTokens()`) is still valid.
    const sendClearOnSignOut = async () => {
      if (cancelled) return;
      // Nothing has ever gone out to this frame, so there is nothing to
      // clear -- sending one now would be exactly the "absence mistaken for
      // sign-out" case the wire contract forbids (see utils/embedAuth.ts).
      if (!identitySentRef.current) return;
      const origin = lastValidatedOriginRef.current;
      const frame = iframeRef.current?.contentWindow;

      if (!origin || !frame) return;

      const freshTokens = await getCognitoTokens();

      if (cancelled || !freshTokens) return;

      // Exact origin, never '*' -- same rule as the regular send below.
      frame.postMessage(
        { type: EMBED_AUTH_TOKEN_TYPE, ...freshTokens, identity_cleared: true },
        origin,
      );
    };
    const unregisterSignOutHandler = onEmbedSignOut(sendClearOnSignOut);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== EMBED_AUTH_REQUEST_TYPE) return;
      // Both checks matter: the origin proves who sent it, the source proves it
      // came from the frame we rendered rather than a sibling or nested frame.
      if (!isTrustedMsr2Origin(event.origin)) {
        console.warn(
          `[embedAuth] ignoring auth request from untrusted origin: ${event.origin}`,
        );

        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        console.warn(
          '[embedAuth] ignoring auth request that did not come from our iframe',
        );

        return;
      }

      // Reply to the origin that asked (already validated), not the configured
      // one -- in non-prod they differ when embedding a PR preview / sandbox.
      // Remember it so a later identity resend (below) has somewhere trusted
      // to go.
      lastValidatedOriginRef.current = event.origin;
      void sendTokens(event.origin);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      window.removeEventListener('message', handleMessage);
      unregisterSignOutHandler();
    };
  }, [isEnabled, iframeRef]);

  // `currentUser` (and so `identity`) can resolve after MSR2 has already
  // been sent tokens without it -- the next scheduled refresh is up to
  // MAX_REFRESH_INTERVAL_MS away, which would leave the embed's GTM without
  // identity for the rest of that window. Re-send as soon as identity goes
  // from absent to present, changes to a different user, or (APP-2232) goes
  // from present back to absent -- a sign-out -- so the embed picks up the
  // change promptly rather than waiting on a scheduled refresh. This never
  // sends the very first message on its own (nothing to resend before
  // `lastValidatedOriginRef` has a value) and never re-sends for an
  // unchanged identity or a re-render alone.
  const lastResentIdentityRef = useRef<null | string>(null);

  useEffect(() => {
    if (!isEnabled) return;

    const serialised = serialiseIdentity(identity);

    if (serialised === lastResentIdentityRef.current) return;
    lastResentIdentityRef.current = serialised;

    // A truthy `serialised` is a normal identity update. A `null` one is
    // only worth a send if identity had actually gone out before --
    // `identitySentRef` is what tells that apart from the initial
    // not-yet-resolved state, which must never be sent as a "cleared".
    const isMeaningfulClear = serialised === null && identitySentRef.current;

    if ((serialised || isMeaningfulClear) && lastValidatedOriginRef.current) {
      void sendTokensRef.current?.(lastValidatedOriginRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, currentUser]);
};
